/*
 * infinite-scroll.js
 * Chargement continu des pages de collection, à la place de la pagination.
 *
 * Le composant <infinite-scroll> est rendu par main-collection-product-grid
 * sous la grille, avec l'URL de la page suivante. Quand il approche de
 * l'écran, il récupère cette page via l'API de rendu de section (la même que
 * facets.js) et en ajoute les cartes à #product-grid.
 *
 * - Sans JavaScript, le lien « Voir plus de posters » reste un vrai lien
 *   rel="next" : la navigation fonctionne, et les robots trouvent les pages.
 * - Filtres et tri : facets.js remplace tout #ProductGridContainer, donc ce
 *   composant est détruit puis recréé avec la bonne URL. Un chargement en
 *   cours est annulé à la destruction.
 * - Animations d'apparition : on ne relance l'observateur de Dawn que sur les
 *   NOUVELLES cartes. Sur toute la grille, il remettrait les cartes déjà vues
 *   au-dessus de l'écran en état « hors écran », donc invisibles.
 */
if (!customElements.get('infinite-scroll')) {
  customElements.define(
    'infinite-scroll',
    class InfiniteScroll extends HTMLElement {
      connectedCallback() {
        this.grille = document.getElementById('product-grid');
        this.bouton = this.querySelector('.infinite-scroll__bouton');
        this.chargeur = this.querySelector('.infinite-scroll__chargeur');
        this.annonce = this.querySelector('.infinite-scroll__annonce');
        this.enCours = false;

        // Sans grille ou sans IntersectionObserver : le lien classique suffit.
        if (!this.grille || !this.bouton || !('IntersectionObserver' in window)) return;

        this.bouton.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.charger();
        });

        // On déclenche un peu avant la fin de la grille, pour que les cartes
        // soient déjà là quand on y arrive.
        this.observateur = new IntersectionObserver(
          (entrees) => {
            if (entrees.some((e) => e.isIntersecting)) this.charger();
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        this.observateur.observe(this);
        this.classList.add('infinite-scroll--auto');
      }

      disconnectedCallback() {
        this.observateur?.disconnect();
        this.controleur?.abort();
      }

      async charger() {
        const suivante = this.dataset.nextUrl;
        if (this.enCours || !suivante) return;

        this.enCours = true;
        this.classList.remove('infinite-scroll--erreur');
        this.classList.add('infinite-scroll--chargement');
        this.chargeur?.classList.remove('hidden');
        this.controleur = new AbortController();

        try {
          const url = new URL(suivante, window.location.origin);
          url.searchParams.set('section_id', this.dataset.sectionId);

          const reponse = await fetch(url, { signal: this.controleur.signal });
          if (!reponse.ok) throw new Error(`HTTP ${reponse.status}`);
          const page = new DOMParser().parseFromString(await reponse.text(), 'text/html');

          const cartes = Array.from(page.querySelectorAll('#product-grid > .grid__item'));
          this.ajouter(cartes);

          const pageSuivante = page.querySelector('infinite-scroll')?.dataset.nextUrl;
          this.annoncer(cartes.length, !pageSuivante);

          if (pageSuivante) {
            this.dataset.nextUrl = pageSuivante;
            this.bouton.href = pageSuivante;
            this.terminerChargement();
            // L'observateur ne se redéclenche pas si le composant est resté
            // visible (grand écran, page courte) : on le réobserve.
            this.observateur.unobserve(this);
            this.observateur.observe(this);
          } else {
            // Plus rien à charger : on retire le lien et l'observateur.
            this.observateur.disconnect();
            delete this.dataset.nextUrl;
            this.bouton.remove();
            this.terminerChargement();
            this.classList.add('infinite-scroll--fin');
          }
        } catch (erreur) {
          if (erreur.name === 'AbortError') return;
          // Échec réseau : le lien réapparaît pour réessayer à la main.
          this.terminerChargement();
          this.classList.add('infinite-scroll--erreur');
        }
      }

      ajouter(cartes) {
        if (!cartes.length) return;
        // Conteneur provisoire : l'observateur des animations de Dawn ne voit
        // ainsi que les nouvelles cartes, qui gardent leur observation une fois
        // déplacées dans la grille.
        const provisoire = document.createElement('div');
        cartes.forEach((carte) => provisoire.appendChild(document.importNode(carte, true)));
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(provisoire);
        }
        this.grille.append(...provisoire.children);
      }

      annoncer(nombre, derniere) {
        if (!this.annonce) return;
        // Textes fournis par layout/theme.liquid (window.rpStrings) dans la langue de la page.
        const t = window.rpStrings || {};
        const modele = nombre > 1
          ? t.postersAjoutesPlusieurs || '[nombre] posters ajoutés.'
          : t.postersAjoutesUn || '[nombre] poster ajouté.';
        const message = modele.replace('[nombre]', nombre);
        this.annonce.textContent = derniere ? `${message} ${t.tousAffiches || 'Tous les posters sont affichés.'}` : message;
      }

      terminerChargement() {
        this.enCours = false;
        this.classList.remove('infinite-scroll--chargement');
        this.chargeur?.classList.add('hidden');
      }
    }
  );
}
